/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import useBaseService from '../../../../hooks/services/useBaseService'
import { useSectionFields } from '../../../../hooks/useSectionFields'
import {
  baseOsAddItemsFields,
  baseOsDynamicFields,
} from '../../../../constants/forms/orderServiceFields'
import useForm from '../../../../hooks/useForm'
import {
  getMissingRequiredFields,
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../../utils/objectUtils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useRemoteSelectOptions from '../../../../hooks/useRemoteSelectOptions'
import { entities } from '../../../../constants/entities'
import MovementEnum from '../../../../enums/movement-enum'
import { useOrgan } from '../../../../hooks/useOrgan'
import { useApplication } from '../../../../hooks/useApplication'
import { useAuth } from '../../../../hooks/useAuth'
import useSelectOptions from '../../../../hooks/useSelectOptions'
import useLoader from '../../../../hooks/useLoader'
import { transformFiltersForUrl } from '../../../../utils/transformFiltersForUrl'
import useNotification from '../../../../hooks/useNotification'
import { v4 as uuidv4 } from 'uuid'
import SimpleBody from '../../../../components/forms/SimpleBody'
import { useQuery } from '@tanstack/react-query'
import { all } from 'axios'

export const useOrderServicePageLogic = ({
  isEditPage = false,
  isAddItemPage = false,
  contractAmount = null,
  originalData = {},
}) => {
  const {
    equipmentsData = [],
    productsData = [],
    extraEquipmentsData = [],
  } = originalData

  const navigate = useNavigate()
  const location = useLocation()
  const { selectedOrgan, selectOrgan } = useOrgan()
  const { selectedApplication } = useApplication()
  const { user } = useAuth()
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()

  const { serviceOrderId } = useParams()

  const hasBalance = useMemo(() => {
    const result = (contractAmount?.mensal?.saldo_disponivel_mensal ?? 0) > 0
    console.log('[DEBUG hasBalance]', {
      saldo_disponivel_mensal: contractAmount?.mensal?.saldo_disponivel_mensal,
      result,
    })
    return result
  }, [contractAmount?.mensal?.saldo_disponivel_mensal])

  // Cria um objeto URLSearchParams para pegar os parâmetros da query
  const queryParams = new URLSearchParams(location.search)

  const {
    get: baseServiceGet,
    getByColumn: baseServiceGetByColumn,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)

  const { updateField: updateProps, removeFields } = useSectionFields()

  const [
    orderServiceSection,
    equipmentSection,
    itemSection,
    extraEquipmentsSection,
  ] = baseOsDynamicFields

  const [
    addOrderServiceItemSetion,
    addEquipmentSection,
    addItemSection,
    addExtraEquipmentsSection,
  ] = baseOsAddItemsFields

  const orderForm = isAddItemPage
    ? addOrderServiceItemSetion
    : orderServiceSection

  const equipmentForm = isAddItemPage ? addEquipmentSection : equipmentSection

  const itemForm = isAddItemPage ? addItemSection : itemSection

  const extraForm = isAddItemPage
    ? addExtraEquipmentsSection
    : extraEquipmentsSection

  const {
    formData,
    handleChange: handleOrderServiceChange,
    setFormData: setOrderServiceFormData,
    resetForm: resetOrderServiceForm,
  } = useForm(setDefaultFieldValues([orderForm]), [orderForm])

  // O contrato da URL serve apenas para inicializar o formulário. Depois que
  // o destinatário muda, ele não deve continuar prevalecendo sobre a seleção.
  const [routeContractId, setRouteContractId] = useState(() =>
    queryParams.get('contract_id')
  )
  const contractId = routeContractId || formData?.order?.contract_id?.value

  // 🆕 1) Contrato TEM itens? (sem filtro de quantidade)
  const { data: allContractItemsResult } = useQuery({
    queryKey: ['allContractItems', contractId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.items(contractId),
        {
          filters: transformFiltersForUrl({ deleted_at: false }),
          page: 1,
          perPage: 1,
        }
      )
      return response?.result
    },
    enabled: !!contractId,
    staleTime: 2 * 60 * 1000,
  })

  // 🆕 2) Existe algum item COM disponibilidade? (filtro que o backend já entende)
  const { data: availableContractItemsResult } = useQuery({
    queryKey: ['availableContractItems', contractId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.items(contractId),
        {
          filters: transformFiltersForUrl({
            deleted_at: false,
            available_quantity: { $gt: 0 },
          }),
          page: 1,
          perPage: 1,
        }
      )
      return response?.result
    },
    enabled: !!contractId,
    staleTime: 2 * 60 * 1000,
  })

  // 🆕 Regra de excedente: contrato TEM itens e NENHUM tem disponibilidade
  const allItemsZeroed = useMemo(() => {
    const hasItems = (allContractItemsResult?.total ?? 0) > 0
    const hasAvailable = (availableContractItemsResult?.total ?? 0) > 0
    return hasItems && !hasAvailable
  }, [allContractItemsResult, availableContractItemsResult])

  const {
    formData: equipmentsFormData,
    handleChange: handleEquipmentChange,
    setFormData: setEquipmentsFormData,
    resetForm: resetEquipmentsForm,
  } = useForm(setDefaultFieldValues([equipmentForm]), [equipmentForm])

  const [actionEquipment, setActionEquipment] = useState('')

  const {
    formData: extraEquipmentsFormData,
    handleChange: handleExtraEquipmentChange,
    setFormData: setExtraEquipmentFormData,
    resetForm: resetExtraEquipmentsForm,
  } = useForm(setDefaultFieldValues([extraForm]), [extraForm])

  const [equipmentsFieldsState, setEquipmentsFieldsState] = useState(
    equipmentForm.fields
  )

  const [orderServiceFieldsState, setOrderServiceFieldsState] = useState(
    orderForm.fields
  )

  const {
    formData: itemsFormData,
    handleChange: handleItemChange,
    setFormData: setItemsFormData,
    resetForm: resetItemsForm,
  } = useForm(setDefaultFieldValues([itemForm]), [itemForm])

  const [equipments, setEquipments] = useState([])
  const [deletedEquipment, setDeletedEquipment] = useState([])
  const [extras, setExtras] = useState([])
  const [items, setItems] = useState([])

  const fetchServiceDepartments = useCallback(
    async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })

      const params = {
        filters,
        page,
        perPage,
        sort: { column: 'id', order: 'asc' },
      }

      const response = await baseServiceGet(
        entities.serviceOrderdepartments.get,
        params
      )

      return response.result
    },
    [baseServiceGet]
  )

  const orderServiceSelect = useRemoteSelectOptions({
    queryKey: ['serviceOrder'],
    baseServiceGet,
    entityPath: contractId
      ? entities.contracts.contractServiceOrder(contractId, serviceOrderId)
      : entities.serviceOrders.getById(serviceOrderId),
    mapToOption: (item) => ({ value: item.id, label: item.id }),
    fieldId: 'order.id',
    formData,
    setFormData: setOrderServiceFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const departmentSelectEquipment = useRemoteSelectOptions({
    queryKey: ['serviceOrderEquipmentDepartament'],
    fetcher: fetchServiceDepartments,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
    fieldId: 'equipments.department_id',
    formData: equipmentsFormData,
    setFormData: setEquipmentsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const departmentSelectItems = useRemoteSelectOptions({
    queryKey: ['serviceOrderItemsDepartament'],
    fetcher: fetchServiceDepartments,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
    fieldId: 'items.department_id',
    formData: itemsFormData,
    setFormData: setItemsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const movementTypeSelect = useRemoteSelectOptions({
    queryKey: ['movementTypes'],
    baseServiceGet,
    entityPath: entities.movementTypes.get,
    extraFilters: {
      // * Quando vinher da página de contratos não mostrar o tipo de requisição avulsa
      ...(contractId ? { id: { $ne: MovementEnum.AVULSO } } : {}),
    },
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    fieldId: 'order.movement_type_id',
    formData,
    setFormData: setOrderServiceFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const isDevolution =
    movementTypeSelect.selectedValue?.value === MovementEnum.DEVOLUCAO
  const isSubstitution =
    movementTypeSelect.selectedValue?.value === MovementEnum.SUBSTITUICAO
  const requiresContract =
    !!movementTypeSelect.selectedValue?.value &&
    movementTypeSelect.selectedValue.value !== MovementEnum.AVULSO
  const recipientCustomerId = formData?.order?.recipient_customer_id?.value

  const { data: otherOrganizationsWithCustomerContracts = [], isFetching: isCheckingOtherOrganizations } = useQuery({
    queryKey: [
      'customerContractsInOtherOrganizations',
      user?.id,
      selectedApplication?.id,
      selectedOrgan?.id,
      recipientCustomerId,
    ],
    queryFn: async () => {
      const organizationsResponse = await baseServiceGet(
        entities.users.applicationsAndOrganizations(user.id, selectedApplication.id)
      )
      const organizations = organizationsResponse.result?.data ?? []
      const otherOrganizations = organizations.filter(
        (organization) => String(organization.id) !== String(selectedOrgan?.id)
      )

      const matches = await Promise.all(
        otherOrganizations.map(async (organization) => {
          try {
            const response = await baseServiceGet(entities.contracts.forSelect, {
              filters: {
                organization_id: organization.id,
                $or: [
                  { customer_id: { $eq: recipientCustomerId } },
                  {
                    'recipientCustomers.customer_id': {
                      $in: [recipientCustomerId],
                    },
                  },
                ],
              },
              page: 1,
              perPage: 1,
            })
            const contracts = response.result?.data
            return Array.isArray(contracts) && contracts.length > 0
              ? organization
              : null
          } catch (error) {
            console.error('Erro ao verificar contratos em outra organização:', error)
            return null
          }
        })
      )

      return matches.filter(Boolean)
    },
    enabled:
      requiresContract &&
      !!recipientCustomerId &&
      !!user?.id &&
      !!selectedApplication?.id &&
      !!selectedOrgan?.id,
    staleTime: 60 * 1000,
  })

  const contractSelect = useRemoteSelectOptions({
    queryKey: ['contracts'],
    baseServiceGet,
    columnDataBase: ['number', 'name'],
    extraFilters: {
      organization_id: selectedOrgan?.id,
      ...(requiresContract && recipientCustomerId
        ? {
            $or: [
              { customer_id: { $eq: recipientCustomerId } },
              {
                'recipientCustomers.customer_id': {
                  $in: [recipientCustomerId],
                },
              },
            ],
          }
        : {}),
    },
    defaultSort: { column: 'id', order: 'desc' },
    entityPath: isDevolution ? entities.contracts.get : entities.contracts.forSelect,
    enabled: !requiresContract || !!recipientCustomerId,
    initialId: routeContractId && (!requiresContract || !!recipientCustomerId) ? routeContractId : null,
    fetcherById: routeContractId && (!requiresContract || !!recipientCustomerId)
      ? async (id) => {
          const response = await baseServiceGetByColumn(
            entities.contracts.getById(id)
          )
          return response.result
        }
      : () => null,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.number} - ${item.name}`,
      useOrderServicePageLogic,
    }),
    fieldId: 'order.contract_id',
    formData,
    setFormData: setOrderServiceFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    immediateFetch: true,
    refetchOnWindowFocus: true,
  })

  const contractSelected = contractSelect.rawData.find(
    (c) => c.id === contractSelect.selectedValue?.value
  )

  const recipientCustomerSelect = useRemoteSelectOptions({
    queryKey: ['customers'],
    baseServiceGet,
    columnDataBase: ['id', 'alias'],
    extraFilters: {
      alias: { $nin: ['ALUCOM', 'MOREIA', 'IP', 'AS SISTEMAS'] },
      // * Na SUBSTITUIÇÃO, só mostra clientes que possuem equipamentos alugados ou reservados
      ...(isSubstitution ? { has_equipments_in_use: true } : {}),
    },
    defaultSort: { column: 'id', order: 'desc' },
    entityPath: entities.customers.get,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    fieldId: 'order.recipient_customer_id',
    enabled: true,
    formData,
    setFormData: setOrderServiceFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  })

  const recipeCustomerRef = useRef(recipientCustomerSelect.selectedValue?.value)

  const addressSelect = useRemoteSelectOptions({
    queryKey: ['addresses', recipientCustomerSelect.selectedValue?.value],
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    baseServiceGet,
    entityPath: entities.addresses.get,
    extraFilters: {
      addressable_id: recipientCustomerSelect.selectedValue?.value,
      addressable_type: 'customer',
    },
    defaultSort: { column: 'id', order: 'desc' },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    fieldId: 'order.address_id',
    formData,
    setFormData: setOrderServiceFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!recipientCustomerSelect.selectedValue?.value,
    refetchOnWindowFocus: true,
  })

  const locationSelect = useRemoteSelectOptions({
    queryKey: ['locations', addressSelect.selectedValue?.value],
    baseServiceGet,
    columnDataBase: 'area',
    entityPath: entities.addresses.locations.get(
      addressSelect.selectedValue?.value
    ),
    extraFilters: {
      address_id: addressSelect.selectedValue?.value,
    },
    defaultSort: { column: 'id', order: 'desc' },
    mapToOption: (item) => {
      return {
        value: item.id,
        label: `${item.id} - ${item.formatted_location}`,
      }
    },
    fieldId: 'order.location_id',
    formData,
    setFormData: setOrderServiceFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!addressSelect.selectedValue?.value,
    refetchOnWindowFocus: true,
  })


  // Função robusta para extrair IDs, lidando com objetos, primitivos, nulos ou undefined
  const getSafeId = useCallback((value) => {
    if (value === null || value === undefined) return null
    if (typeof value === 'object') {
      return value.value ?? value.id ?? value.fullData?.id ?? null
    }
    return value
  }, [])

  const calculateMaxAllowed = useCallback(
    ({
      contractItem,
      equipments = [],
      editingItemId = null,
      deletedEquipment = null,
      currentServiceOrderId = null,
    }) => {
      const contractItemId = String(getSafeId(contractItem?.id))

      // Saldo real já calculado pelo backend (considera todos os aluguéis ativos
      // e devoluções confirmadas, de todas as OS)
      const baseAvailable = Number(contractItem?.available_quantity ?? 0)

      // Só relevante em edição: o backend já descontou o que ESTA OS usa do saldo,
      // então somamos de volta pra poder redistribuir dentro da própria OS
      const qtyAlreadyReservedByThisOrder = currentServiceOrderId
        ? (contractItem?.order_items || [])
            .filter(
              (oi) =>
                String(oi.service_order_id) === String(currentServiceOrderId)
            )
            .reduce((sum, oi) => sum + (Number(oi.quantity) || 0), 0)
        : 0

      // 2. Quantidade dos OUTROS itens desta OS (excluindo o que está sendo editado)
      const qtyInCurrentOSOthers = equipments
        .filter((eq) => {
          if (
            editingItemId &&
            String(getSafeId(eq.id)) === String(editingItemId)
          )
            return false
          if (
            deletedEquipment &&
            String(getSafeId(eq.id)) === String(getSafeId(deletedEquipment.id))
          )
            return false

          // Compara usando a função segura
          const eqContractItemId = String(getSafeId(eq.contract_item_id))
          return eqContractItemId === contractItemId
        })
        .reduce((sum, eq) => sum + (Number(eq.quantity) || 0), 0)

      return Math.max(
        0,
        baseAvailable + qtyAlreadyReservedByThisOrder - qtyInCurrentOSOthers
      )
    },
    [getSafeId]
  )

  // Hook base para buscar os dados do contrato
  const contractItemSelect = useRemoteSelectOptions({
    queryKey: ['contractItems', contractId, hasBalance, allItemsZeroed],
    baseServiceGet,
    columnDataBase: 'alias',
    entityPath: entities.contracts.items(contractId),
    extraFilters: {
      // * Em aluguel, manter itens zerados visíveis para permitir uma nova
      // * requisição excedente. Nos demais movimentos, preservar o filtro atual.
      ...(!isEditPage &&
        !isAddItemPage &&
        movementTypeSelect.selectedValue?.value !== MovementEnum.ALUGUEL &&
        movementTypeSelect.selectedValue?.value !== MovementEnum.RESERVA &&
        (hasBalance || !allItemsZeroed) &&
        {
          available_quantity: { $gt: 0 },
        }),
      // * Filtro para não mostrar itens já vinculados a esta requisição
      ...(isAddItemPage &&
        serviceOrderId && {
          $itemNotInCurrentRequest: {
            orderItems: {
              service_order_id: { $eq: Number(serviceOrderId) },
            },
          },
        }),
    },
    fieldId: 'equipments.contract_item_id',
    formData: equipmentsFormData,
    setFormData: setEquipmentsFormData,
    perPage: 15,
    enabled: !!contractId,
    immediateFetch: !!isEditPage,
    mapToOption: (item) => {
      const isReserva =
        movementTypeSelect.selectedValue?.value === MovementEnum.RESERVA
      const labelBase = `${item.alias || item.id} - ${item.description}`

      if (isReserva) {
        return { value: item.id, label: labelBase, fullData: item }
      }

      let remainingQuantity

      // Cenário 1: Item foi deletado e estamos adicionando outro
      if (actionEquipment === 'add' && deletedEquipment) {
        const isDeletedItem =
          String(item.id) ===
            String(deletedEquipment.contract_item_id?.value) ||
          String(item.root_item_id) === String(deletedEquipment.root_item_id)

        if (isDeletedItem) {
          // Recalcula considerando que o item foi removido
          remainingQuantity = calculateMaxAllowed({
            contractItem: item,
            equipments: equipments.filter(
              (eq) => String(eq.id) !== String(deletedEquipment.id)
            ),
            editingItemId: null,
            deletedEquipment: null,
            currentServiceOrderId: serviceOrderId,
          })
        } else {
          remainingQuantity = calculateMaxAllowed({
            contractItem: item,
            equipments,
            editingItemId: null,
            deletedEquipment: null,
            currentServiceOrderId: serviceOrderId,
          })
        }
      }
      // Cenário 2: Uso normal (criar, editar, adicionar)
      else {
        remainingQuantity = calculateMaxAllowed({
          contractItem: item,
          equipments,
          editingItemId: null,
          deletedEquipment,
          currentServiceOrderId: serviceOrderId,
        })
      }

      const label = `${labelBase} (Total: ${item.quantity}, Restante: ${remainingQuantity})`
      return { value: item.id, label, fullData: item }
    },
    action: actionEquipment,
    dependencies: equipments,
    key_dependency: 'root_item_id',
  })

  const modeTransportSelect = useRemoteSelectOptions({
    queryKey: ['serviceOrderModeTransport'],
    baseServiceGet,
    entityPath: entities.serviceOrderModeTransports.get,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    fieldId: 'order.mode_transport_id',
    formData,
    setFormData: setOrderServiceFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const requiresInvoiceSelect = useSelectOptions({
    fieldId: 'order.requires_invoice',
    formData,
    setFormData: setOrderServiceFormData,
    rawOptions: [
      { id: 0, name: 'Não' },
      { id: 1, name: 'Sim' },
    ],
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  })

  const contractSecondRef = useRef(contractSelect.selectedValue?.value)
  const refetchWhenEquipment = useCallback(() => {
    if (isEditPage) return false

    if (contractSecondRef.current !== contractSelect.selectedValue?.value) {
      contractSecondRef.current = contractSelect.selectedValue?.value
      equipmentSelect.reset()
      return true
    }

    // * Recarregar quando o cliente mudar, se o movimento for avulso ou devolução
    if (
      [MovementEnum.AVULSO, MovementEnum.DEVOLUCAO].includes(
        movementTypeSelect.selectedValue?.value
      ) ||
      (movementTypeSelect.selectedValue?.value === MovementEnum.SUBSTITUICAO &&
        !contractSelect.selectedValue?.value)
    ) {
      if (
        recipeCustomerRef.current !==
        recipientCustomerSelect.selectedValue?.value
      ) {
        recipeCustomerRef.current = recipientCustomerSelect.selectedValue?.value
        equipmentSelect.reset()
        return true
      }
    }
    return false
  }, [
    isEditPage,
    contractSelect.selectedValue?.value,
    movementTypeSelect.selectedValue?.value,
    recipientCustomerSelect.selectedValue?.value,
  ])

  // * Quando for devolução, buscar os equipamentos que estão no cliente
  const useCustomerEquipmentList =
    isDevolution ||
    movementTypeSelect.selectedValue?.value === MovementEnum.AVULSO ||
    (isSubstitution && !contractSelected?.id)
  const equipmentSelect = useRemoteSelectOptions({
    queryKey: ['equipments'],
    entityPath: useCustomerEquipmentList
      ? entities.customers.equipments.get(
          recipientCustomerSelect.selectedValue?.value
        )
      : entities.contracts.contractEquipments(contractSelected?.id),
    baseServiceGet,
    columnDataBase: ['name', 'number'],
    mapToOption: (item) => ({
      value: item.id,
      // * Se o utlimo movimento do equipamento foi avulso, mostrar "AVULSO" no status do equipamento ao inves de "ALUGADO"
      label: `${item.last_relevant_movement_item?.order_item?.movement_type_id === MovementEnum.AVULSO ? 'AVULSO' : item.status?.name} | ${item.number} - ${item.name}`,
      isAvulso:
        item.last_relevant_movement_item?.order_item?.movement_type_id ===
        MovementEnum.AVULSO,
    }),
    action: actionEquipment,
    dependencies: equipments,
    fieldId: 'equipments.equipment_id',
    enabled: [MovementEnum.ALUGUEL, MovementEnum.RESERVA].includes(
      movementTypeSelect.selectedValue?.value
    )
      ? !!contractSelected?.id
      : isSubstitution
        ? !!contractSelected?.id || !!recipientCustomerSelect.selectedValue?.value
        : !!recipientCustomerSelect.selectedValue?.value,
    formData: equipmentsFormData,
    setFormData: setEquipmentsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchWhen: refetchWhenEquipment,
  })


  const typesSelect = useRemoteSelectOptions({
    queryKey: ['equipmentTypes'],
    baseServiceGet,
    entityPath: entities.types.get,
    extraFilters: {
      'products.is_asset': { $eq: 1 },
      ...(isAddItemPage &&
        serviceOrderId && {
          // * Retorna os items que não estão numa requisição virgente
          $itemNotInCurrentRequest: {
            orderItems: {
              service_order_id: { $eq: Number(serviceOrderId) },
            },
          },
        }),
    },
    fieldId: 'equipments.type_id',
    isMulti: false,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    formData: equipmentsFormData,
    setFormData: setEquipmentsFormData,
    perPage: 15,
    action: actionEquipment,
    dependencies: equipments,
    enablePagination: true,
  })

  const refetchWhenAddressByCustomer = useCallback(() => {
    if (
      recipeCustomerRef.current !==
      recipientCustomerSelect.selectedValue?.value
    ) {
      recipeCustomerRef.current = recipientCustomerSelect.selectedValue?.value
      return true
    }
    return false
  }, [recipientCustomerSelect.selectedValue?.value])

  const addressSelectEquipment = useRemoteSelectOptions({
    queryKey: ['addresses', recipientCustomerSelect.selectedValue?.value],
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    baseServiceGet,
    entityPath: entities.addresses.get,
    extraFilters: {
      addressable_id: recipientCustomerSelect.selectedValue?.value,
      addressable_type: 'customer',
    },
    defaultSort: { column: 'id', order: 'desc' },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    fieldId: 'equipments.address_id',
    formData: equipmentsFormData,
    setFormData: setEquipmentsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!recipientCustomerSelect.selectedValue?.value,
    refetchOnWindowFocus: true,
    refetchWhen: refetchWhenAddressByCustomer,
  })

  const addressSelectItems = useRemoteSelectOptions({
    queryKey: ['addresses', recipientCustomerSelect.selectedValue?.value],
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    baseServiceGet,
    entityPath: entities.addresses.get,
    extraFilters: {
      addressable_id: recipientCustomerSelect.selectedValue?.value,
      addressable_type: 'customer',
    },
    defaultSort: { column: 'id', order: 'desc' },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    fieldId: 'items.address_id',
    formData: itemsFormData,
    setFormData: setItemsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!recipientCustomerSelect.selectedValue?.value,
    refetchOnWindowFocus: true,
    refetchWhen: () => {
      if (
        recipeCustomerRef.current !==
        recipientCustomerSelect.selectedValue?.value
      ) {
        recipeCustomerRef.current = recipientCustomerSelect.selectedValue?.value
        return true
      }
      return false
    },
  })

  const locationSelectEquipment = useRemoteSelectOptions({
    queryKey: ['locations', addressSelectEquipment.selectedValue?.value],
    baseServiceGet,
    columnDataBase: 'area',
    entityPath: entities.addresses.locations.get(
      addressSelectEquipment.selectedValue?.value
    ),
    extraFilters: {
      address_id: addressSelectEquipment.selectedValue?.value,
    },
    defaultSort: { column: 'id', order: 'desc' },
    mapToOption: (item) => {
      return {
        value: item.id,
        label: `${item.id} - ${item.formatted_location}`,
      }
    },
    fieldId: 'equipments.location_id',
    formData: equipmentsFormData,
    setFormData: setEquipmentsFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!addressSelectEquipment.selectedValue?.value,
    refetchOnWindowFocus: true,
  })

  const locationSelectItems = useSelectOptions({
    fieldId: 'items.location_id',
    formData: itemsFormData,
    setFormData: setOrderServiceFormData,
    rawOptions: locationSelectEquipment.rawData,
    mapToOption: (item) => {
      return {
        value: item.id,
        label: `${item.id} - ${item.formatted_location}`,
      }
    },
  })

  const fetchContractDevolutionEquipments = useCallback(async (recipientCustomerId) => {
    if (!recipientCustomerId) return []
    const perPage = 100
    const all = []
    let page = 1
    let total = Infinity
    while (all.length < total) {
      const response = await baseServiceGet(
        entities.customers.equipments.get(recipientCustomerId),
        { page, perPage }
      )
      const result = response?.result
      const rows = Array.isArray(result) ? result : result?.data ?? []
      all.push(...rows)
      total = Number(result?.total ?? all.length)
      if (!rows.length) break
      page += 1
    }
    return all
  }, [baseServiceGet])


  const [actionItem, setActionItem] = useState()

  const productsSelect = useRemoteSelectOptions({
    queryKey: ['almox_products'],
    baseServiceGet,
    entityPath: entities.products.get,
    extraFilters: {
      is_asset: { $eq: false },
      // 'items.organization_id': { $eq: selectedOrgan?.id },
    },
    fieldId: 'items.product_id',
    isMulti: false,
    // Busca do saldo total do produto, somando o estoque dos lotes em 'items' ou usando o campo 'total_quantity' vindo do backend
    mapToOption: (item) => {
      const totalStock = Array.isArray(item.items)
        ? item.items.reduce(
            (acc, curr) => acc + (Number(curr.quantity) || 0),
            0
          )
        : Number(item.total_quantity || item.quantity) || 0

      return {
        value: item.id,
        // 2. Formata o label exibindo o Nome + Quantidade em Estoque
        label: `${item.name} (Estoque: ${totalStock})`,
      }
    },
    formData: itemsFormData,
    setFormData: setItemsFormData,
    perPage: 15,
    action: actionItem,
    dependencies: items,
    enablePagination: true,
  })

  // Kit de Equipamentos
  const equipmentTypeSelectExtras = useRemoteSelectOptions({
    queryKey: ['equipmentTypes'],
    baseServiceGet,
    entityPath: entities.types.get,
    extraFilters: {
      'products.is_asset': { $eq: 1 },
      is_kit: { $eq: 1 },
    },
    isMulti: false,
    fieldId: 'extras.type_id',
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    formData: extraEquipmentsFormData,
    setFormData: setExtraEquipmentFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const equipmentReferenceSelectExtras = useSelectOptions({
    fieldId: 'extras.equipment_id',
    formData: extraEquipmentsFormData,
    setFormData: setExtraEquipmentFormData,
    rawOptions: equipments,
    mapToOption: (item) => ({
      value: item.id,
      label:
        `${item.contract_item_id?.label.replace(/\s*\(.*?\)\s*/g, '').trim()} - Qtd: ${item.quantity}` ||
        'Equipamento sem nome',
    }),
  })

  const selects = useMemo(
    () => ({
      'order.id': orderServiceSelect,
      'order.contract_id': contractSelect,
      'order.recipient_customer_id': recipientCustomerSelect,
      // "order.destination_order_id": destinationsOrderSelect,
      'order.mode_transport_id': modeTransportSelect,
      'order.movement_type_id': movementTypeSelect,
      'order.address_id': addressSelect,
      'order.location_id': locationSelect,
      'order.requires_invoice': requiresInvoiceSelect,
    }),
    [
      orderServiceSelect,
      contractSelect,
      recipientCustomerSelect,
      // destinationsOrderSelect,
      modeTransportSelect,
      movementTypeSelect,
      addressSelect,
      locationSelect,
      requiresInvoiceSelect,
    ]
  )

  const getOrderServiceOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getOrderServiceSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const getOrderServiceSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const equipmentsSelects = useMemo(
    () => ({
      'equipments.department_id': departmentSelectEquipment,
      'equipments.contract_item_id': contractItemSelect,
      'equipments.type_id': typesSelect,
      'equipments.equipment_id': equipmentSelect,
      'equipments.address_id': addressSelectEquipment,
      'equipments.location_id': locationSelectEquipment,
    }),
    [
      departmentSelectEquipment,
      contractItemSelect,
      typesSelect,
      equipmentSelect,
      addressSelectEquipment,
      locationSelectEquipment,
    ]
  )

  const getEquipmentsOptions = useCallback(
    (fieldId) => {
      const selectConfig = equipmentsSelects[fieldId]
      return selectConfig?.options || []
    },
    [equipmentsSelects]
  )

  const getEquipmentsSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = equipmentsSelects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [equipmentsSelects]
  )

  const getEquipmentsSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = equipmentsSelects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [equipmentsSelects]
  )

  const itemsSelects = useMemo(
    () => ({
      'items.department_id': departmentSelectItems,
      'items.product_id': productsSelect,
      'items.address_id': addressSelectItems,
      'items.location_id': locationSelectItems,
    }),
    [
      departmentSelectItems,
      productsSelect,
      addressSelectItems,
      locationSelectItems,
    ]
  )

  const getItemsOptions = useCallback(
    (fieldId) => {
      const selectConfig = itemsSelects[fieldId]
      return selectConfig?.options || []
    },
    [itemsSelects]
  )

  const getItemsSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = itemsSelects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [itemsSelects]
  )

  const getItemsSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = itemsSelects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [itemsSelects]
  )

  const extrasSelects = useMemo(
    () => ({
      'extras.equipment_id': equipmentReferenceSelectExtras,
      'extras.type_id': equipmentTypeSelectExtras,
    }),
    [equipmentReferenceSelectExtras, equipmentTypeSelectExtras]
  )

  const getExtrasOptions = useCallback(
    (fieldId) => {
      const selectConfig = extrasSelects[fieldId]
      return selectConfig?.options || []
    },
    [extrasSelects]
  )

  const getExtrasSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = extrasSelects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [extrasSelects]
  )

  const getExtrasSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = extrasSelects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [extrasSelects]
  )

  const initializeEquipmentsForm = useCallback(
    (equipmentData) => {
      // Se não tiver dados de equipamento (novo item), aplica o defaultValue
      const dataToSet = equipmentData || {
        department_id: departmentSelectEquipment.selectedValue || {
          value: 2,
          label: 'EXPEDIÇÃO',
        },
      }

      setEquipmentsFormData({ equipments: dataToSet || {} })
    },
    [departmentSelectEquipment.selectedValue, setEquipmentsFormData]
  )

  const initializeExtrasForm = useCallback(
    (extraData) => {
      setExtraEquipmentFormData({ extras: extraData || {} })
    },
    [setExtraEquipmentFormData]
  )

  const initializeItemsForm = useCallback(
    (itemData) => {
      const dataToSet = itemData || {
        department_id: departmentSelectItems?.selectedValue || {
          value: 1,
          label: 'ALMOXARIFADO',
        },
      }
      setItemsFormData({ items: dataToSet || {} })
    },
    [departmentSelectItems?.selectedValue, setItemsFormData]
  )

  const getItemsDisplayValue = useCallback((item, fieldId) => {
    const fieldName = fieldId.split('.').pop()
    const value = item[fieldName]

    if (value && typeof value === 'object' && 'label' in value) {
      // se value.label tiver parenteses, remova tudo do paranteses em diante (para remover o (Total: 50, Restante: 50) dos itens do contrato)
      if (value.label.includes('(')) {
        return value.label.split('(')[0].trim()
      }
      return value.label
    }

    if ((value && typeof value === 'undefined') || !value) {
      return ''
    }

    return value
  }, [])

  const handleEquipmentsChange = useCallback(
    (newEquipments) => {
      setEquipments(newEquipments)
      setOrderServiceFormData((prev) => ({
        ...prev,
        equipments: newEquipments,
      }))
    },
    [setOrderServiceFormData]
  )

  const addDevolutionEquipments = useCallback((selected) => {
    const existingIds = new Set(equipments.map((item) => String(getSafeId(item.equipment_id))))
    const additions = selected.filter((equipment) => !existingIds.has(String(equipment.id))).map((equipment) => ({
      id: uuidv4(),
      equipment_id: { value: equipment.id, label: `${equipment.number ?? ''} - ${equipment.name ?? ''}`.trim() },
      department_id: null,
      quantity: 1,
      details: null,
      address_id: addressSelectEquipment.selectedValue ?? null,
      location_id: locationSelectEquipment.selectedValue ?? null,
    }))
    if (additions.length) handleEquipmentsChange([...equipments, ...additions])
  }, [equipments, getSafeId, handleEquipmentsChange, addressSelectEquipment.selectedValue, locationSelectEquipment.selectedValue])

  const handleExtrasChange = useCallback(
    (newExtras) => {
      setExtras(newExtras)
      setOrderServiceFormData((prev) => ({ ...prev, extras: newExtras }))
    },
    [setOrderServiceFormData]
  )

  const handleSaveEquipments = useCallback(
    (itemToEdit) => {
      const itemData = equipmentsFormData.equipments
      const typeMovement = movementTypeSelect.selectedValue?.value

      // Validação de campos obrigatórios (mantém sua lógica original)
      const ignoreMap = {
        aluguel: ['equipments.equipment_id', 'equipments.type_id'],
        devolucao: [
          'equipments.contract_item_id',
          'equipments.type_id',
          'equipments.quantity',
        ],
        substituicao: [
          'equipments.contract_item_id',
          'equipments.type_id',
          'equipments.quantity',
        ],
        reserva: ['equipments.equipment_id', 'equipments.type_id'],
        avulso: ['equipments.equipment_id', 'equipments.contract_item_id'],
      }

      const missingFields = getMissingRequiredFields({
        itemData,
        fields: equipmentForm?.fields,
        prefix: 'equipments',
        ignore:
          typeMovement === MovementEnum.ALUGUEL
            ? ignoreMap.aluguel
            : typeMovement === MovementEnum.DEVOLUCAO
              ? ignoreMap.devolucao
              : typeMovement === MovementEnum.SUBSTITUICAO
                ? ignoreMap.substituicao
                : typeMovement === MovementEnum.RESERVA
                  ? ignoreMap.reserva
                  : typeMovement === MovementEnum.AVULSO
                    ? ignoreMap.avulso
                    : [],
      })

      if (missingFields.length > 0) {
        showNotification(
          'warning',
          'Preencha todos os campos obrigatórios para equipamento'
        )
        return false
      }

      // 🔧 Helper único de excedente: usa o saldo REAL do item de contrato
      // selecionado (contractItem.available_quantity), não um agregado do
      // contrato inteiro (allItemsZeroed) nem o saldo mensal (hasBalance).
      const isItemExcedente = (contractItem) =>
        typeMovement === MovementEnum.ALUGUEL &&
        Number(contractItem?.available_quantity ?? 0) <= 0

      if (typeMovement === MovementEnum.ALUGUEL) {
        const requestedQty = Number(itemData?.quantity) || 0

        // Usa a função segura para pegar o ID correto
        const safeContractItemId = getSafeId(itemData.contract_item_id)

        // Busca APENAS pelo ID exato, sem fallbacks perigosos de null
        const contractItem = contractItemSelect.rawData?.find(
          (ci) => String(ci.id) === String(safeContractItemId)
        )

        if (contractItem) {
          const maxAllowed = calculateMaxAllowed({
            contractItem,
            equipments,
            editingItemId: itemToEdit?.id,
            deletedEquipment,
            currentServiceOrderId: serviceOrderId,
          })

          // 🔧 antes: const isExcedente = !hasBalance && allItemsZeroed
          const isExcedente = isItemExcedente(contractItem)

          // * Se o item de contrato específico estiver com saldo zerado, permitir excedente
          if (!isExcedente) {
            if (requestedQty > maxAllowed) {
              showNotification(
                'warning',
                `Quantidade excede o total disponível (${maxAllowed}).`
              )
              return false
            }
          }

          if (requestedQty <= 0) {
            showNotification('warning', 'A quantidade deve ser maior que zero.')
            return false
          }
        }
      }

      const updatedEquipments = itemToEdit
        ? equipments.map((item) => {
            if (String(getSafeId(item.id)) === String(itemToEdit.id)) {
              const updatedItem = { ...item, ...itemData }
              const safeContractItemId = getSafeId(itemData.contract_item_id)

              const contractItem = contractItemSelect.rawData?.find(
                (ci) => String(ci.id) === String(safeContractItemId)
              )

              if (contractItem) {
                const maxAllowed = calculateMaxAllowed({
                  contractItem,
                  equipments,
                  editingItemId: itemToEdit.id,
                  deletedEquipment,
                  currentServiceOrderId: serviceOrderId,
                })

                const finalQuantity = Number(updatedItem.quantity) || 0
                const remainingQuantity = maxAllowed - finalQuantity
                const isReserva =
                  movementTypeSelect.selectedValue?.value ===
                  MovementEnum.RESERVA
                const labelBase = `${contractItem.alias || contractItem.id} - ${contractItem.description}`

                updatedItem.contract_item_id = {
                  ...itemData.contract_item_id,
                  label: isReserva
                    ? labelBase
                    : `${labelBase} (Total: ${contractItem.quantity}, Restante: ${remainingQuantity})`,
                }

                // 🔧 NOVO: recalcula is_exchange na edição, usando o saldo
                // atual do item de contrato — não deixa o valor congelado
                // do momento da criação.
                updatedItem.is_exchange = isItemExcedente(contractItem)
              }
              return updatedItem
            }
            return item
          })
        : (() => {
            const safeContractItemId = getSafeId(itemData.contract_item_id)

            const contractItem = contractItemSelect.rawData?.find(
              (ci) => String(ci.id) === String(safeContractItemId)
            )

            if (!contractItem) {
              return [...equipments, { id: uuidv4(), ...itemData }]
            }
            console.log('[DEBUG EXCEDENTE - Frontend]', {
              contract_item_id: contractItem.id,
              available_quantity: contractItem.available_quantity,
              isItemExcedente_result: isItemExcedente(contractItem),
              valor_que_será_salvo_como_is_exchange:
                isItemExcedente(contractItem),
            })
            const maxAllowed = calculateMaxAllowed({
              contractItem,
              equipments,
              editingItemId: null,
              deletedEquipment,
              currentServiceOrderId: serviceOrderId,
            })

            const finalQuantity = Number(itemData.quantity) || 0
            const remainingQuantity = maxAllowed - finalQuantity
            const isReserva =
              movementTypeSelect.selectedValue?.value === MovementEnum.RESERVA
            const labelBase = `${contractItem.alias || contractItem.id} - ${contractItem.description}`

            return [
              ...equipments,
              {
                id: uuidv4(),
                ...itemData,
                attended_quantity: contractItem.attended_quantity,
                root_item_id: contractItem.root_item_id,
                contract_item_id: {
                  ...itemData.contract_item_id,
                  label: isReserva
                    ? labelBase
                    : `${labelBase} (Total: ${contractItem.quantity}, Restante: ${remainingQuantity})`,
                },
                // 🔧 antes: is_exchange: !hasBalance && allItemsZeroed,
                is_exchange: isItemExcedente(contractItem),
              },
            ]
          })()

      setEquipments(updatedEquipments)
      handleEquipmentsChange(updatedEquipments)
      return true
    },
    [
      equipmentsFormData.equipments,
      movementTypeSelect.selectedValue?.value,
      equipmentForm?.fields,
      equipments,
      contractItemSelect.rawData,
      showNotification,
      handleEquipmentsChange,
    ]
  )

  const handleSaveExtras = useCallback(
    (itemToEdit) => {
      const itemData = extraEquipmentsFormData.extras

      const missingFields = getMissingRequiredFields({
        itemData,
        fields: extraEquipmentsSection.fields,
        prefix: 'extras',
      })

      if (missingFields.length > 0) {
        showNotification(
          'warning',
          'Preencha todos os campos obrigatórios para o equipmento extra'
        )
        return false
      }

      // A quantidade de kits é independente da quantidade do equipamento pai.
      // 1. Verificar se já existe esse tipo para esse pai na lista
      const existingIndex = extras.findIndex((item) => {
        if (itemToEdit && item.id === itemToEdit.id) return false

        const savedEqId = item.equipment_id?.value || item.equipment_id
        const savedTypeId = item.type_id?.value || item.type_id
        return (
          savedEqId === itemData.equipment_id?.value &&
          savedTypeId === itemData.type_id?.value
        )
      })

      const currentExtraQuantity = Number(itemData.quantity)

      if (existingIndex !== -1) {
        // ITEM JÁ EXISTE: Vamos tentar somar
        const existingItem = extras[existingIndex]
        const newTotalQuantity =
          Number(existingItem.quantity) + currentExtraQuantity

        // Atualiza o item existente somando as quantidades, sem limitar pelo pai.
        const updatedExtras = [...extras]
        updatedExtras[existingIndex] = {
          ...existingItem,
          quantity: String(newTotalQuantity), // Mantém como string se o seu form usar string
        }

        setExtras(updatedExtras)
        handleExtrasChange(updatedExtras)
        return true
      }

      // 2. ITEM NOVO: quantidade não depende da quantidade do equipamento pai.
      const updatedExtras = itemToEdit
        ? extras.map((item) =>
            item.id === itemToEdit.id ? { ...item, ...itemData } : item
          )
        : [...extras, { id: uuidv4(), ...itemData }]

      setExtras(updatedExtras)
      handleExtrasChange(updatedExtras)
      return true
    },
    [
      extraEquipmentsFormData.extras,
      extraEquipmentsSection.fields,
      extras,
      equipments,
      setExtras,
      handleExtrasChange,
      showNotification,
    ]
  )

  const handleItemsChange = useCallback(
    (newItems) => {
      setItems(newItems)
      setOrderServiceFormData((prev) => ({ ...prev, items: newItems }))
    },
    [setOrderServiceFormData]
  )

  const handleSaveItems = useCallback(
    (itemToEdit) => {
      const itemData = itemsFormData.items

      const missingFields = getMissingRequiredFields({
        itemData,
        fields: itemSection.fields,
        prefix: 'items',
        ignore: [],
      })

      if (missingFields.length > 0) {
        showNotification('warning', 'Preencha todos os campos obrigatórios')
        return false
      }

      const currentItems = Array.isArray(items) ? items : [] // ← guard

      const updatedItems = itemToEdit
        ? currentItems.map((item) =>
            item.id === itemToEdit.id ? { ...item, ...itemData } : item
          )
        : [...currentItems, { id: uuidv4(), ...itemData }]

      setItems(updatedItems)
      handleItemsChange(updatedItems)
      return true
    },
    [
      itemsFormData.items,
      itemSection.fields,
      items,
      handleItemsChange,
      showNotification,
    ]
  )

  const renderFormBodyEquipment = useCallback(
    ({
      fields,
      formData,
      handleFieldChange,
      formErrors,
      getOptions,
      getSelectedValue,
      getSelectMeta,
    }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getOptions}
          getSelectedValue={getSelectedValue}
          getSelectMeta={getSelectMeta}
        />
      )
    },
    []
  )

  const renderFormBodyExtras = useCallback(
    ({
      fields,
      formData,
      handleFieldChange,
      formErrors,
      getOptions,
      getSelectedValue,
      getSelectMeta,
    }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getOptions}
          getSelectedValue={getSelectedValue}
          getSelectMeta={getSelectMeta}
        />
      )
    },
    []
  )

  const renderFormBodyItems = useCallback(
    ({
      fields,
      formData,
      handleFieldChange,
      formErrors,
      getOptions,
      getSelectedValue,
      getSelectMeta,
    }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getOptions}
          getSelectedValue={getSelectedValue}
          getSelectMeta={getSelectMeta}
        />
      )
    },
    []
  )

  const applyMovementRules = (fields, typeMovement) => {
    let result = [...fields]

    switch (typeMovement) {
      case MovementEnum.SUBSTITUICAO:
        result = updateProps({
          arrayDependente: result,
          field_id: result.map((f) => f.id),
          updates: { disabled: false },
        })

        // result = removeFields({
        //   arrayDependente: result,
        //   field_id: ["order.destination_order_id"],
        // });

        result = updateProps({
          arrayDependente: result,
          field_id: ['order.deadline'],
          updates: { fullWidth: true },
        })

        result = updateProps({
          arrayDependente: result,
          field_id: ['equipments.equipment_id'],
          updates: { fullWidth: false },
        })

        result = removeFields({
          arrayDependente: result,
          field_id: [
            'equipments.type_id',
            'equipments.contract_item_id',
            'equipments.quantity',
          ],
        })

        break
      case MovementEnum.DEVOLUCAO:
        result = updateProps({
          arrayDependente: result,
          field_id: result.map((f) => f.id),
          updates: { disabled: false },
        })

        result = updateProps({
          arrayDependente: result,
          field_id: ['order.contract_id'],
          updates: { notRequired: true },
        })

        // result = removeFields({
        //   arrayDependente: result,
        //   field_id: ["order.destination_order_id"],
        // });

        result = updateProps({
          arrayDependente: result,
          field_id: ['equipments.equipment_id'],
          updates: { fullWidth: false },
        })

        result = removeFields({
          arrayDependente: result,
          field_id: [
            'equipments.type_id',
            'equipments.contract_item_id',
            'equipments.quantity',
          ],
        })

        result = updateProps({
          arrayDependente: result,
          field_id: ['order.deadline'],
          updates: { fullWidth: isAddItemPage },
        })

        break

      case MovementEnum.ALUGUEL:
      case MovementEnum.RESERVA:
        result = updateProps({
          arrayDependente: result,
          field_id: result.map((f) => f.id),
          updates: { disabled: false },
        })

        result = removeFields({
          arrayDependente: result,
          field_id: ['equipments.equipment_id', 'equipments.type_id'],
        })

        // * Quando for na página de edição da requisição, bloqueiar o campo de contract_item_id, para que quando a ação for de "edit"
        // * não permitir trocar o contract_item_id, somente corrigir as informações ou remover.
        if (actionEquipment === 'edit') {
          result = updateProps({
            arrayDependente: result,
            field_id: ['equipments.contract_item_id'],
            updates: { disabled: true },
          })
        }

        break
      case MovementEnum.AVULSO:
        result = updateProps({
          arrayDependente: result,
          field_id: result.map((f) => f.id),
          updates: { disabled: false },
        })

        result = removeFields({
          arrayDependente: result,
          field_id: ['order.contract_id'],
        })

        result = removeFields({
          arrayDependente: result,
          field_id: ['equipments.equipment_id', 'equipments.contract_item_id'],
        })

        result = updateProps({
          arrayDependente: result,
          field_id: ['order.deadline'],
          updates: { fullWidth: isAddItemPage },
        })

        break

      default:
        result = updateProps({
          arrayDependente: result,
          field_id: result.map((f) => f.id),
          updates: { disabled: false },
        })
    }

    if (typeMovement !== MovementEnum.AVULSO) {
      const hasRecipientCustomer = !!formData?.order?.recipient_customer_id?.value
      result = updateProps({
        arrayDependente: result,
        field_id: ['order.contract_id'],
        updates: {
          disabled: !hasRecipientCustomer,
          placeholder: hasRecipientCustomer
            ? 'Selecione um contrato deste cliente'
            : 'Selecione o cliente destinatário primeiro',
          helperText: hasRecipientCustomer
            ? 'A lista mostra contratos vinculados ao cliente selecionado.'
            : 'Escolha o cliente destinatário para liberar a seleção de contrato.',
        },
      })
    }

    return result
  }

  const applyContextRules = (fields) => {
    let result = [...fields]

    // ADD
    if (isAddItemPage) {
      return updateProps({
        arrayDependente: result,
        field_id: result.map((f) => f.id),
        updates: { disabled: true },
      })
    }

    // EDIT (SUA REGRA CRÍTICA)
    if (isEditPage) {
      const locked = [
        'order.movement_type_id',
        'order.contract_id',
        'order.recipient_customer_id',
      ]

      result = updateProps({
        arrayDependente: result,
        field_id: locked,
        updates: { disabled: true },
      })
    }

    return result
  }

  useEffect(() => {
    const typeMovement = movementTypeSelect.selectedValue?.value

    const base = orderForm.fields ?? []
    const equipment = equipmentForm?.fields ?? []

    let orderResult = applyMovementRules(base, typeMovement)
    let equipmentResult = applyMovementRules(equipment, typeMovement)

    if (typeMovement !== MovementEnum.DEVOLUCAO) {
      const isAvulsoEquipment = (equipmentItem) => {
        const selectedEquipment = equipmentItem?.equipment_id
        if (typeof selectedEquipment?.isAvulso === 'boolean') {
          return selectedEquipment.isAvulso
        }

        const equipmentId = getSafeId(selectedEquipment)
        const rawEquipment = equipmentSelect.rawData?.find(
          (item) => String(item.id) === String(equipmentId)
        )
        return (
          rawEquipment?.last_relevant_movement_item?.order_item
            ?.movement_type_id === MovementEnum.AVULSO
        )
      }
      const allSubstitutionEquipmentsAreAvulso =
        typeMovement === MovementEnum.SUBSTITUICAO &&
        equipments.length > 0 &&
        equipments.every(isAvulsoEquipment)

      orderResult = updateProps({
        arrayDependente: orderResult,
        field_id: ['order.contract_id'],
        updates: { notRequired: allSubstitutionEquipmentsAreAvulso },
      })
    }

    orderResult = applyContextRules(orderResult)

    setOrderServiceFieldsState(orderResult)
    setEquipmentsFieldsState(equipmentResult)
  }, [
    movementTypeSelect.selectedValue,
    formData?.order?.recipient_customer_id?.value,
    orderForm.fields,
    equipmentForm?.fields,
    equipments,
    equipmentSelect.rawData,
    getSafeId,
    isAddItemPage,
    isEditPage,
    actionEquipment,
  ])

  const prevMovementType = useRef(movementTypeSelect.selectedValue?.value)
  const prevContract = useRef(contractSelect.selectedValue?.value)
  const prevClient = useRef(recipientCustomerSelect.selectedValue?.value)
  const prevAddress = useRef(addressSelect.selectedValue?.value)

  // * Limpa os estados locais das listas e formulários quando mudar o tipo de movimento
  useEffect(() => {
    const currentMovement = movementTypeSelect.selectedValue?.value
    const currentContract = contractSelect.selectedValue?.value
    const currentClient = recipientCustomerSelect.selectedValue?.value
    const currentAddress = addressSelect.selectedValue?.value

    const resetListData = () => {
      setEquipments([])
      setExtras([])
      setItems([])
      resetEquipmentsForm()
      resetExtraEquipmentsForm()
      resetItemsForm()
    }

    // 1. Se mudar o TIPO DE MOVIMENTO: Limpa TUDO abaixo dele
    if (
      prevMovementType.current &&
      prevMovementType.current !== currentMovement
    ) {
      resetOrderServiceForm() // Limpa Contrato, Cliente, Endereço, etc.
      resetListData()

      // Mantém o novo movimento selecionado após o reset global
      handleOrderServiceChange(
        'order.movement_type_id',
        movementTypeSelect.selectedValue
      )
    }

      // 2. Se mudar o CONTRATO: mantém o cliente e limpa os dados dependentes
      else if (prevContract.current && prevContract.current !== currentContract) {
      // handleOrderServiceChange("order.destination_order_id", null);
      handleOrderServiceChange('order.address_id', null)
      handleOrderServiceChange('order.location_id', null)
      handleOrderServiceChange('order.mode_transport_id', null)
      handleOrderServiceChange('order.deadline', null)
      handleOrderServiceChange('order.details', null)

      resetListData()
    }

    // 3. Se mudar o CLIENTE: Limpa apenas o que for dependente de cliente e as listas
    else if (prevClient.current && prevClient.current !== currentClient) {
      if (currentMovement !== MovementEnum.AVULSO) {
        // Libera a busca de contratos para o novo cliente em vez de manter
        // fixado o contrato recebido originalmente pela URL.
        setRouteContractId(null)
        handleOrderServiceChange('order.contract_id', null)
      }
      // handleOrderServiceChange("order.destination_order_id", null);
      handleOrderServiceChange('order.address_id', null)
      handleOrderServiceChange('order.location_id', null)
      handleOrderServiceChange('order.mode_transport_id', null)
      handleOrderServiceChange('order.deadline', null)
      handleOrderServiceChange('order.details', null)
    }

    // 4. Se mudar o ENDEREÇO: Limpa apenas o que for dependente de endereço e as listas
    else if (prevAddress.current && prevAddress.current !== currentAddress) {
      handleOrderServiceChange('order.location_id', null)
    }

    // Atualiza as refs para a próxima renderização
    prevMovementType.current = currentMovement
    prevContract.current = currentContract
    prevClient.current = currentClient
    prevAddress.current = currentAddress
  }, [
    movementTypeSelect.selectedValue,
    contractSelect.selectedValue,
    recipientCustomerSelect.selectedValue,
    addressSelect.selectedValue,
    resetOrderServiceForm,
    resetEquipmentsForm,
    resetExtraEquipmentsForm,
    resetItemsForm,
    handleOrderServiceChange,
    isDevolution,
    isEditPage, // ← adicionar na dep array
  ])

  // * Hidrata no formulário apenas o contrato inicial recebido pela URL.
  useEffect(() => {
    const typeSelected = movementTypeSelect.selectedValue?.value
    if (!routeContractId || !typeSelected) return
    if (requiresContract && !recipientCustomerId) return

    // Procuramos o contrato no rawData (inclui o item inicial da URL).
    const contract = contractSelect.rawData.find(
      (c) => c.id === Number(routeContractId)
    )

    if (contract) {
      const selectedContract = {
        value: contract.id,
        label: `${contract.number} - ${contract.name}`,
      }

      const selectedOrg = {
        value: contract.organization?.id,
        label: contract.organization?.alias || 'Organização', // Certifique-se que o backend retorna o nome
      }

      // 3. Atualizamos o formulário apenas se os valores forem diferentes
      if (
        formData.order?.contract_id?.value !== contract.id ||
        formData.order?.organization_id?.value !== contract.organization_id
      ) {
        setOrderServiceFormData((prev) => ({
          ...prev,
          order: {
            ...prev.order,
            organization_id: selectedOrg.value
              ? selectedOrg
              : prev.order.organization_id,
            contract_id: selectedContract,
          },
        }))
      }
    }
  }, [
    routeContractId,
    requiresContract,
    recipientCustomerId,
    movementTypeSelect.selectedValue?.value,
    contractSelect.rawData,
    formData.order?.contract_id?.value,
    formData.order?.organization_id?.value,
    setOrderServiceFormData,
  ])

  // * Enquanto os dados do contrato estiverem sendo buscados, seta um loading
  useEffect(() => {
    if (contractSelect.isFetching && !contractSelect.rawData.length) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [
    contractSelect.isFetching,
    contractSelect.rawData.length,
    hideLoader,
    showLoader,
  ])

  // * Se houver uma mensagem na URL, mostra a notificação
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const handleEquipmentDelete = useCallback(
    (equipmentDeleted) => {
      setDeletedEquipment(equipmentDeleted)
      // Filtra a lista de Kit de Equipamentos para remover os que pertencem ao equipamento excluído
      // Supondo que o campo de vínculo no extra seja 'equipment_id'
      const updatedExtras = extras.filter(
        (extra) =>
          extra.equipment_id.value !==
            equipmentDeleted.contract_item_id.value &&
          extra.equipment_id.value !== equipmentDeleted.id
      )

      setExtras(updatedExtras)
      handleExtrasChange(updatedExtras) // Atualiza o formulário global
    },
    [extras, setExtras, handleExtrasChange]
  )

  const isDisabledButton = useMemo(() => {
    if (typeof recipientCustomerSelect.selectedValue?.value !== 'undefined') {
      return false
    }
    return true
  }, [recipientCustomerSelect.selectedValue])

  return {
    fetchContractDevolutionEquipments,
    addDevolutionEquipments,
    contract: contractSelected,
    otherOrganizationsWithCustomerContracts,
    isCheckingOtherOrganizations,
    selectOrgan,
    form: {
      errors: formErrors,
      setErrors: setFormErrors,
      contract: contractSelected,
      orgao: selectedOrgan,
      disabledButton: isDisabledButton,
    },
    order: {
      section: orderServiceSection.section,
      description: orderServiceSection.description,
      fields: orderServiceSection.fields,
      formData: formData,
      fieldsState: orderServiceFieldsState,
      setFieldsState: setOrderServiceFieldsState,
      handleChange: handleOrderServiceChange,
      setFormData: setOrderServiceFormData,
      resetOrderServiceForm: resetOrderServiceForm,
      getOptions: getOrderServiceOptions,
      getSelectedValue: getOrderServiceSelectedValue,
      getSelectMeta: getOrderServiceSelectMeta,
      hasBalance: hasBalance,
      allItemsZeroed: allItemsZeroed,
      selects: {
        contract: contractSelect,
        recipientCustomer: recipientCustomerSelect,
        movementType: movementTypeSelect,
        modeTransport: modeTransportSelect,
      },
    },
    equipments: {
      data: equipments,
      setData: setEquipments,
      section: equipmentSection.section,
      description: equipmentSection.description,
      fields: equipmentForm?.fields,
      formData: equipmentsFormData,
      fieldsState: equipmentsFieldsState,
      setFieldsState: setEquipmentsFieldsState,
      handleChange: handleEquipmentChange,
      setFormData: setEquipmentsFormData,
      resetOrderServiceForm: resetEquipmentsForm,
      getOptions: getEquipmentsOptions,
      getSelectedValue: getEquipmentsSelectedValue,
      getSelectMeta: getEquipmentsSelectMeta,
      onItemsChange: handleEquipmentsChange, // * Para o componente de adição de item modificado, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
      onFormSubmit: handleSaveEquipments, // * Para salvar as alterações feitas no formulário de adição de item modificado
      initializeItemForm: initializeEquipmentsForm, // * Para inicializar o formulário de adição de item modificado com os dados de um item existente
      renderFormBody: renderFormBodyEquipment, // * Para renderizar o corpo do formulário de adição de item modificado
      getItemDisplayValue: getItemsDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de item modificado
      captureAction: setActionEquipment,
      onDeleteItem: handleEquipmentDelete,
      selects: {
        contractItem: contractItemSelect,
        departament: departmentSelectEquipment,
        type: typesSelect,
        equipment: equipmentSelect,
        address: addressSelectEquipment,
        location: locationSelectEquipment,
      },
      equipments,
      setEquipments,
    },
    extras: {
      data: extras,
      setData: setExtras,
      section: extraEquipmentsSection.section,
      description: extraEquipmentsSection.description,
      fields: extraEquipmentsSection.fields,
      formData: extraEquipmentsFormData,
      fieldsState: [],
      setFieldsState: () => {},
      handleChange: handleExtraEquipmentChange,
      setFormData: setExtraEquipmentFormData,
      resetOrderServiceForm: resetExtraEquipmentsForm,
      getOptions: getExtrasOptions,
      getSelectedValue: getExtrasSelectedValue,
      getSelectMeta: getExtrasSelectMeta,
      onItemsChange: handleExtrasChange, // * Para o componente de adição de item modificado, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
      onFormSubmit: handleSaveExtras, // * Para salvar as alterações feitas no formulário de adição de item modificado
      initializeItemForm: initializeExtrasForm, // * Para inicializar o formulário de adição de item modificado com os dados de um item existente
      renderFormBody: renderFormBodyExtras, // * Para renderizar o corpo do formulário de adição de item modificado
      getItemDisplayValue: getItemsDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de item modificado
      selects: {
        equipment: equipmentReferenceSelectExtras,
        type: equipmentTypeSelectExtras,
      },
      extras,
      setExtras,
    },
    items: {
      data: items,
      setData: setItems,
      section: itemSection.section,
      description: itemSection.description,
      fields: itemSection.fields,
      formData: itemsFormData,
      fieldsState: [],
      setFieldsState: () => {},
      handleChange: handleItemChange,
      setFormData: setItemsFormData,
      resetOrderServiceForm: resetItemsForm,
      getOptions: getItemsOptions,
      getSelectedValue: getItemsSelectedValue,
      getSelectMeta: getItemsSelectMeta,
      onItemsChange: handleItemsChange, // * Para o componente de adição de item modificado, que precisa atualizar o estado local e o formulário principal ao mesmo tempo
      onFormSubmit: handleSaveItems, // * Para salvar as alterações feitas no formulário de adição de item modificado
      initializeItemForm: initializeItemsForm, // * Para inicializar o formulário de adição de item modificado com os dados de um item existente
      renderFormBody: renderFormBodyItems, // * Para renderizar o corpo do formulário de adição de item modificado
      getItemDisplayValue: getItemsDisplayValue, // * Para obter o valor de exibição correto para os campos do aditivo de item modificado
      captureAction: setActionItem,
      selects: {
        product: productsSelect,
        departament: departmentSelectItems,
        address: addressSelectItems,
        location: locationSelectItems,
      },
      items,
      setItems,
    },
  }
}
